import React, { Component, Fragment } from 'react';
import { Switch, Route, withRouter } from 'react-router-dom';
import ViewsContainer from '../../components/views/ViewsContainer';
import CalendarDetail from './calendarDetail';
import LoadingWizard from '../../components/spinner/wizard';
import moment from 'moment';
import AddCalendarModal from './modals/addCalendarModal';

class BookingCalendars extends Component {
	constructor(props) {
		super(props);

		const fieldColumns = {
			name: 'Calendar Name',
			location: 'Location',
			startDate: 'Start Date',
			endDate: 'End Date',
			availabilitySummary: 'Availability',
			bookingsCount: 'Booked Spots',
			requestCount: 'Requests',
			uniqueRegistrantCount: 'Registrants',
			availableSpots: 'Available Spots',
			status: 'Status',
		};

		const fieldTypes = {
			name: 'string',
			location: 'string',
			availabilitySummary: 'string',
			startDate: 'date',
			endDate: 'date',
			requestCount: 'number',
			uniqueRegistrantCount: 'number',
			availableSpots: 'string',
			bookingsCount: 'number',
			status: 'string',
			createdAt: 'timestamp',
			updatedAt: 'timestamp'
		};

		const fieldCategories = {
			basics: {
				title: 'Calendar Info',
				columns: [
					'name',
					'location',
					'availabilitySummary',
					'startDate',
					'endDate',
					'requestCount',
					'uniqueRegistrantCount',
					'availableSpots',
					'bookingsCount',
					'status',
					'createdAt',
					'updatedAt'
				]
			}
		};

		this.state = {
			columns: fieldColumns,
			columnTypes: fieldTypes,
			categoryColumns: fieldCategories,
			sidePanelOpen: false,
			loading: false,
			activeRow: '',
			addCalendarModalOpen: false,
			deleteCalendarsModalOpen: false,
		};
	}

	addCalendar = (newCalendar, cb) => {

		this.props.addCalendar(newCalendar, (createdCalendar) => {
			// Calendar was created successfully
			cb && cb(createdCalendar);
		});
	};

	deleteCalendars = (calendarIds, callback) => {
		this.props.deleteCalendars(calendarIds, () => {
			if (this.state.sidePanelOpen) {
				this.props.history.push('./');
			}
			callback && callback();
		});
	};

	// Helper function to format availability summary
	formatAvailabilitySummary = (schedule) => {
		if (!schedule) return 'Not set';

		const dayAbbreviations = {
			monday: 'Mo',
			tuesday: 'Tu',
			wednesday: 'We',
			thursday: 'Th',
			friday: 'Fr',
			saturday: 'Sa',
			sunday: 'Su'
		};

		const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
		const timeRangeGroups = {};

		// Group days by their time ranges
		dayOrder.forEach(dayId => {
			if (schedule[dayId]?.available && dayAbbreviations[dayId]) {
				const timeRanges = schedule[dayId].availableTimeRanges || [];
				if (timeRanges.length > 0) {
					// Create a key from the time ranges for grouping
					const timeKey = timeRanges.map(range => {
						const startTime = moment(range.startTime, 'h:mm A').format('hA');
						const endTime = moment(range.endTime, 'h:mm A').format('hA');
						return `${startTime}-${endTime}`;
					}).join(',');

					if (!timeRangeGroups[timeKey]) {
						timeRangeGroups[timeKey] = [];
					}
					timeRangeGroups[timeKey].push(dayAbbreviations[dayId]);
				}
			}
		});

		if (Object.keys(timeRangeGroups).length === 0) return 'Not available';

		// Format the output
		const summaryParts = Object.keys(timeRangeGroups).map(timeKey => {
			const days = timeRangeGroups[timeKey];
			return `${days.join(',')} ${timeKey}`;
		});

		return summaryParts.join('; ');
	};

	// Helper function to calculate pending request count for a calendar
	calculateRequestCount = (calendar, requests) => {
		if (!requests || !Array.isArray(requests)) return 0;

		let pendingCount = 0;
		const uniqueRegistrants = new Set();

		requests.forEach(request => {
			if (request.selectedDateTimes) {
				Object.keys(request.selectedDateTimes).forEach(date => {
					const calendarTimes = request.selectedDateTimes[date]?.[calendar._id];
					if (calendarTimes && Array.isArray(calendarTimes)) {
						// Check if any time slots are still pending (not approved or denied)
						const hasPendingSlots = calendarTimes.some(timeSlot => {
							const isApproved = request.approvedTimes?.[date]?.[calendar._id]?.includes(timeSlot);
							const isDenied = request.deniedTimes?.[date]?.[calendar._id]?.includes(timeSlot);
							return !isApproved && !isDenied;
						});

						if (hasPendingSlots) {
							// Add unique registrant
							const registrantId = request.userId || request.submission?.formData?.email || request._id;
							uniqueRegistrants.add(registrantId);
						}
					}
				});
			}
		});

		return uniqueRegistrants.size;
	};

	// Helper function to calculate unique registrant count for a calendar
	calculateUniqueRegistrantCount = (calendar, requests) => {
		if (!requests || !Array.isArray(requests)) return 0;

		const uniqueRegistrants = new Set();

		requests.forEach(request => {
			if (request.selectedDateTimes) {
				Object.keys(request.selectedDateTimes).forEach(date => {
					const calendarTimes = request.selectedDateTimes[date]?.[calendar._id];
					if (calendarTimes && Array.isArray(calendarTimes) && calendarTimes.length > 0) {
						// Add unique registrant regardless of approval status
						const registrantId = request.userId || request.submission?.formData?.email || request._id;
						uniqueRegistrants.add(registrantId);
					}
				});
			}
		});

		return uniqueRegistrants.size;
	};

	// Helper function to calculate available spots
	calculateAvailableSpots = (calendar) => {
		if (!calendar.limitSpots) return 'Unlimited';

		const { bookingPage } = this.props;
		if (!bookingPage || !calendar.schedule) return '0 available';

		const startDate = moment(calendar.startDate);
		const endDate = moment(calendar.endDate);
		const maxSpots = calendar.maxSpots || 1;
		let totalAvailableSpots = 0;

		// Iterate through each day in the calendar range
		let currentDate = moment(startDate);
		while (currentDate.isSameOrBefore(endDate)) {
			const dayOfWeek = currentDate.format('dddd').toLowerCase();
			const dayConfig = calendar.schedule[dayOfWeek];

			// Check if this day is available and has time ranges
			if (dayConfig?.available && dayConfig.availableTimeRanges?.length > 0) {
				const dateStr = currentDate.format('YYYY-MM-DD');

				// Process each available time range for this day
				dayConfig.availableTimeRanges.forEach((range) => {
					const startTime = moment(`${dateStr} ${range.startTime}`, 'YYYY-MM-DD h:mm A');
					const endTime = moment(`${dateStr} ${range.endTime}`, 'YYYY-MM-DD h:mm A');

					// Generate time slots within this range
					let slotTime = moment(startTime);
					while (slotTime.isBefore(endTime)) {
						const slotTimeStr = slotTime.format('h:mm A');

						// Count existing bookings for this specific time slot
						const bookingsCount = calendar.bookedSpots?.filter(
							spot => {
								return spot.date === dateStr &&
									spot.time === slotTimeStr &&
									spot.status !== 'canceled';
							}
						)?.length || 0;

						// Calculate available spots for this time slot
						const availableInSlot = Math.max(0, maxSpots - bookingsCount);
						totalAvailableSpots += availableInSlot;

						// Move to next slot
						slotTime.add(bookingPage.meetingDuration, bookingPage.durationUnit);
					}
				});
			}

			currentDate.add(1, 'day');
		}

		return totalAvailableSpots > 0 ? `${totalAvailableSpots} available` : '0 available';
	};

	render() {
		const { calendars, bookingPage, orgId } = this.props;
		const { columns, columnTypes, categoryColumns, sidePanelOpen, loading, activeRow } = this.state;

		// Process calendars for display (no link property for side panel pattern)
		const processedCalendars = calendars.map(calendar => ({
			...calendar,
			timeSlots: calendar.timeSlots ? calendar.timeSlots.length : 0,
			dateRange: calendar.dateRange
				? `${moment(calendar.dateRange.start).format('MMM D')} - ${moment(calendar.dateRange.end).format('MMM D, YYYY')}`
				: 'Not set',
			status: calendar.status || 'active',
			createdAt: calendar.createdAt || new Date().toISOString(),
			// New columns
			availabilitySummary: this.formatAvailabilitySummary(calendar.schedule),
			startDate: calendar.startDate || null,
			endDate: calendar.endDate || null,
			requestCount: this.calculateRequestCount(calendar, bookingPage.requests),
			uniqueRegistrantCount: this.calculateUniqueRegistrantCount(calendar, bookingPage.requests),
			availableSpots: this.calculateAvailableSpots(calendar)
		}));

		return (
			<Switch>
				<Route path={`/${orgId}/booking/${bookingPage._id}/calendars`}>
					<Fragment>
						<ViewsContainer
							activeRow={activeRow}
							orgId={orgId}
							views={[]} // Add calendar views when backend is implemented
							entries={processedCalendars}
							columns={columns}
							columnTypes={columnTypes}
							categoryColumns={categoryColumns}
							title="Booking Calendars"
							description="Manage availability and time slots for your booking page"
							readOnly={false}
							mainActions={[{
								type: 'button',
								onClick: () => this.setState({ addCalendarModalOpen: true }),
								label: 'Add Calendar',
								class: 'actionButton actionPrimary'
							}]}
							batchActions={[
								{
									label: 'Delete',
									iconClass: 'las la-trash-alt',
									class: 'danger',
									onClick: this.deleteCalendars
								}
							]}
							columnWidths={{
								location: 180,
								availabilitySummary: 300,
								startDate: 120,
								endDate: 120,
								requestCount: 100,
								uniqueRegistrantCount: 120,
								availableSpots: 140,
								bookingsCount: 150,
								status: 100,
							}}
							type={'BookingCalendar'}
							referenceCollection={'BookingPages'}
							referenceId={bookingPage._id}
							emptyStateTitle="No Calendars Yet"
							emptyStateDescription="Create your first calendar to define available time slots for bookings. Calendars help you organize your availability."
							emptyStateIcon="las la-calendar-plus"
							emptyStateAction={{
								label: 'Create First Calendar',
								onClick: () => this.setState({ addCalendarModalOpen: true }),
							}}
							breadcrumbs={[
								{
									name: bookingPage.name || 'Booking Page',
								}
							]}
							section={'calendars'}
						/>

						<div className={`sidePanelContainer ${sidePanelOpen ? 'panelOpen' : ''}`}>
							<Switch>
								<Route path={`/${orgId}/booking/${bookingPage._id}/calendars/:calendarId`}
									render={(props) => {
										const calendarId = props.match.params.calendarId;
										const selectedCalendar = calendars.find(c => c._id === calendarId);
										if (!sidePanelOpen) this.setState({ sidePanelOpen: true });

										return (
											<div className='h-100' key={calendarId}>
												{!loading && selectedCalendar && (
													<CalendarDetail
														calendar={selectedCalendar}
														bookingPage={bookingPage}
														orgId={orgId}
														saveCalendar={this.props.saveCalendar}
														onApproveTimeSlot={this.props.approveRequestTimeSlot}
														cancelBooking={this.props.cancelBooking}
														updateBookedTimeslot={this.props.updateBookedTimeslot}
														onDenyTimeSlot={this.props.denyRequestTimeSlot}
														eventVenue={this.props.eventVenue}
													/>
												)}
												{!selectedCalendar && !loading && (
													<div className="p-20">Calendar not found</div>
												)}
												<LoadingWizard loading={loading} />
											</div>
										);
									}}
								/>
								<Route exact path={`/${orgId}/booking/${bookingPage._id}/calendars`}
									render={() => {
										if (sidePanelOpen) this.setState({ sidePanelOpen: false });
										return <></>;
									}}
								/>
							</Switch>
						</div>
						{this.state.addCalendarModalOpen && (
							// Modal component to add new calendar
							<AddCalendarModal
								isOpen={this.state.addCalendarModalOpen}
								toggle={() => this.setState({ addCalendarModalOpen: false })}
								addCalendar={this.addCalendar}
							/>
						)}
					</Fragment>
				</Route>
			</Switch>
		);
	}
}

export default withRouter(BookingCalendars);
